import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../ui/dialog";
import Button from "../../form/button";
import { Input } from "../../ui/input";
import { IExpense, IExpenseListRecord, IExpenseRecord, IExpenseTypeRecord, Status } from "../../../lib/types";
import pb from "../../../lib/pocketbase";
import ErrorMessage from "../../form/errorMessage";
import SelectType from "./selectType";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import Confirmation from "../../form/Confirmation";

interface NewExpenseProps {
    expense: IExpenseRecord;
    reload?: () => void;
    defaultList?: string;
    open: boolean;
    setOpen: (x: boolean) => void;
}
export default function EditExpense({ expense, reload, open, setOpen, defaultList }: NewExpenseProps) {
    const [types, setTypes] = useState<IExpenseTypeRecord[]>([]);
    const [addToList, setAddToList] = useState(false);
    const [lists, setLists] = useState<IExpenseListRecord[]>([]);
    const [name, setName] = useState("");
    const [price, setPrice] = useState<number>();
    const [amount, setAmount] = useState<number>(1);
    const [status, setStatus] = useState<Status>("none");
    const [error, setError] = useState("");
    const [type, setType] = useState<IExpenseTypeRecord>();
    const [date, setDate] = useState(new Date().toISOString());
    const [income, setIncome] = useState(false);
    const [list, setList] = useState<string>();

    async function loadTypes() {
        const t: IExpenseTypeRecord[] = await pb.collection("expenseType").getFullList();
        setTypes(t);
    }

    async function loadLists() {
        const l: IExpenseListRecord[] = await pb.collection("expenseList").getFullList({
            filter: "archive=false"
        });
        setLists(l);
    }

    useEffect(() => {
        loadTypes();
        loadLists();
    }, []);

    useEffect(() => {
        setName(expense.name);
        setPrice(expense.price);
        setAmount(expense.amount);
        setType(expense.expand.type);
        setDate(new Date(expense.date).toISOString());
        setIncome(expense.income);
        if (expense.list) {
            setList(expense.list);
            setAddToList(true);
        }
    }, [expense]);

    function edit() {
        if (!pb.authStore.model) return;
        if (!price) {
            setError("Fill in the price");
            return;
        }
        if (!type) {
            setError("Expense must have a category");
            return;
        }
        setStatus("loading");
        const data: IExpense = {
            name: name,
            amount: amount,
            price: price,
            type: type.id,
            user: pb.authStore.model.id,
            date: date,
            income: income,
            list: defaultList || list || ""
        };

        pb.collection("expense")
            .update(expense.id, data, {
                expand: "type"
            })
            .then(() => {
                reload && reload();
                setTimeout(() => {
                    setStatus("success");
                    setError("");
                    setOpen(false);
                }, 500);
            })
            .catch((err: Error) => {
                setStatus("error");
                setError(err.message);
            })
            .finally(() => {
                setTimeout(() => {
                    setStatus("none");
                }, 3000);
            });
    }

    function deleteExpense() {
        pb.collection("expense")
            .delete(expense.id)
            .then(() => {
                reload && reload();
                setOpen(false);
            });
    }
    return (
        <Dialog
            open={open}
            onOpenChange={(x) => {
                setOpen(x);
            }}>
            <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="mt-8 md:mt-0">
                <DialogHeader>
                    <DialogTitle>Edit expense</DialogTitle>
                </DialogHeader>
                <ErrorMessage error={error} close={() => setError("")} />

                <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && edit()} placeholder="Name of the expense" />
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center">
                        <div className="flex h-9 w-12 items-center justify-center rounded-l border text-slate-500">€</div>
                        <Input min={0} className="rounded-l-none" type="number" value={price || ""} onChange={(e) => setPrice(parseFloat(e.target.value))} onKeyDown={(e) => e.key === "Enter" && edit()} placeholder="Price" />
                    </div>
                    <div className="flex items-center">
                        <Input min={0} className="rounded-r-none" type="number" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value))} onKeyDown={(e) => e.key === "Enter" && edit()} placeholder="Amount" />
                        <div className="flex h-9 w-12 items-center justify-center rounded-r border text-slate-500">#</div>
                    </div>
                </div>
                <hr className="my-2" />
                <SelectType type={type} types={types} setType={(t) => setType(t)} />
                <Input className="" type="date" value={new Date(date)?.toISOString()?.substring(0, 10)} onChange={(e) => setDate(e.target.value)} />
                <p className="flex items-center gap-2">
                    This is an income: <Switch checked={income} onCheckedChange={setIncome} />
                </p>
                {!defaultList && (
                    <>
                        {!addToList ? (
                            <Button onClick={() => setAddToList(true)} className="w-fit">
                                Add to a list
                            </Button>
                        ) : (
                            <div className="flex items-center gap-1">
                                <Select value={list || undefined} onValueChange={(l) => setList(l)}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select list" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-56">
                                        {lists?.map((list) => (
                                            <SelectItem value={list.id}>{list.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button onClick={() => setList(undefined)}>X</Button>
                            </div>
                        )}
                    </>
                )}
                <DialogFooter className="gap-2">
                    <Confirmation title="Delete this expense?" description="Are you sure you wanna delete this expense?" onConfirm={deleteExpense}>
                        <Button size={"box"}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                />
                            </svg>
                        </Button>
                    </Confirmation>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button status={status} onClick={() => edit()} variant="dark">
                        {status === "loading" && "Editing"}
                        {status === "success" && "Edited"}
                        {status === "error" && "Error"}
                        {status === "none" && "Edit"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
